/**
 * NotificationTemplateEditor
 *
 * Full-page editor for notification email templates. The body editor is an
 * HTML source editor (monospace Textarea) — admins write email-safe HTML
 * directly, insert variables via the shared picker, and preview
 * sanitized/rendered output.
 *
 * Per-template custom variables are still supported as an advanced option
 * for variables that don't exist in the global registry.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronRight,
  IconCode,
  IconDeviceFloppy,
  IconEye,
  IconFileText,
  IconMail,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';

import { useMedplum } from '@medplum/react-hooks';
import { notify } from '@/lib/notify';
import { usePermissions } from '@/contexts/PermissionContext';
import { canSendTestEmail } from '@/pages/settings/system-integrations/systemIntegrationsAccess';
import { VariableTextInput, VariableTextarea } from '@/lib/template-variables/components/VariableTextInput';
import { VariablePickerPopover } from '@/lib/template-variables/components/VariablePickerPopover';
import { registerNotificationVariables } from '../notification-variables';
import { sendTestEmail } from '../services/notification-runtime';
import type { NotificationTemplateConfig } from '../notification-template.types';

import type {
  NotificationTemplateDraft,
  NotificationTemplateVariable,
} from '../notification-template.types';
import { TEMPLATE_STATUS_OPTIONS } from '../notification-template.constants';
import { validateNotificationDraft } from '../services/notification-validation';
import { wrapEmailHtml } from '../services/notification-email-html';
import { renderPreviewWithCatalogDefaults } from '../services/notification-variable-resolver';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface NotificationTemplateEditorProps {
  initial: NotificationTemplateDraft;
  onSave: (draft: NotificationTemplateDraft) => Promise<NotificationTemplateDraft>;
  onBack: () => void;
  saving: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationTemplateEditor({
  initial,
  onSave,
  onBack,
  saving,
}: NotificationTemplateEditorProps): JSX.Element {
  useEffect(() => {
    registerNotificationVariables();
  }, []);

  const [draft, setDraft] = useState<NotificationTemplateDraft>(() => ({
    ...initial,
    from: { ...initial.from },
    variables: initial.variables.map((v) => ({ ...v })),
  }));
  const [activeTab, setActiveTab] = useState<string>('editor');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const update = useCallback((patch: Partial<NotificationTemplateDraft>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleSave = async () => {
    const result = validateNotificationDraft(draft);
    if (!result.valid) {
      const errs: Record<string, string> = {};
      for (const e of result.errors) errs[e.path] = e.message;
      setValidationErrors(errs);
      return;
    }
    setValidationErrors({});
    const saved = await onSave(draft);
    setDraft(saved);
  };

  const preview = useMemo(
    () => renderPreviewWithCatalogDefaults(draft.subject, draft.htmlBody, draft.textBody, draft.variables),
    [draft.subject, draft.htmlBody, draft.textBody, draft.variables],
  );

  const sanitizedPreviewHtml = useMemo(
    () => wrapEmailHtml(preview.htmlBody),
    [preview.htmlBody],
  );

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center" wrap="wrap" gap="md">
          <Group gap="sm">
            <ActionIcon variant="subtle" color="gray" onClick={onBack}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={3}>{draft.id ? 'Edit Template' : 'New Template'}</Title>
              <Text size="sm" c="dimmed">
                {draft.id ? `Editing "${draft.name || 'Untitled'}"` : 'Create a new email notification template'}
              </Text>
            </div>
          </Group>
          <Button
            color="gold"
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={saving}
          >
            Save
          </Button>
        </Group>

        <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? 'editor')}>
          <Tabs.List>
            <Tabs.Tab value="editor" leftSection={<IconCode size={14} />}>Editor</Tabs.Tab>
            <Tabs.Tab value="preview" leftSection={<IconEye size={14} />}>Preview (sanitized)</Tabs.Tab>
          </Tabs.List>

          {/* ======================== Editor Tab ======================== */}
          <Tabs.Panel value="editor" pt="lg">
            <Stack gap="lg">
              {/* Metadata */}
              <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                  <Title order={5}>Template Details</Title>
                  <Group grow gap="md" align="flex-start">
                    <TextInput
                      label="Name"
                      placeholder="e.g. Appointment Confirmation"
                      value={draft.name}
                      onChange={(e) => update({ name: e.currentTarget.value })}
                      error={validationErrors['name']}
                      required
                    />
                    <Select
                      label="Status"
                      data={TEMPLATE_STATUS_OPTIONS}
                      value={draft.status}
                      onChange={(v) => update({ status: (v as NotificationTemplateDraft['status']) ?? 'draft' })}
                    />
                  </Group>
                  <TextInput
                    label="Description"
                    placeholder="Briefly describe what this template does"
                    value={draft.description}
                    onChange={(e) => update({ description: e.currentTarget.value })}
                  />
                </Stack>
              </Paper>

              {/* Sender */}
              <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                  <Title order={5}>Sender</Title>
                  <Text size="sm" c="dimmed" mb="xs">
                    Used as the From address on send. Each address must be listed in the Medplum
                    project secret smtpApprovedSenders and on your Paubox-verified domain. If unset,
                    Medplum uses the project smtpFromAddress default.
                  </Text>
                  <Group grow gap="md" align="flex-start">
                    <TextInput
                      label="From name"
                      description="Display name shown to recipients"
                      placeholder="Unify Mental Health"
                      value={draft.from.name ?? ''}
                      onChange={(e) => update({ from: { ...draft.from, name: e.currentTarget.value } })}
                    />
                    <TextInput
                      label="From email"
                      description="Must appear in smtpApprovedSenders"
                      placeholder="notifications@unifymentalhealth.com"
                      value={draft.from.email ?? ''}
                      onChange={(e) => update({ from: { ...draft.from, email: e.currentTarget.value } })}
                      error={validationErrors['from.email']}
                    />
                  </Group>
                </Stack>
              </Paper>

              {/* Email Content */}
              <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                  <Title order={5}>Email Content</Title>

                  {/* Subject — uses shared VariableTextInput */}
                  <VariableTextInput
                    label="Subject"
                    placeholder="e.g. Your appointment with {{provider.name}} is confirmed"
                    value={draft.subject}
                    onChange={(next) => update({ subject: next })}
                    error={validationErrors['subject']}
                    required
                  />

                  {/* HTML body editor */}
                  <Box>
                    <Group justify="space-between" align="center" mb={4}>
                      <Text size="sm" fw={500}>
                        HTML Body <Text component="span" c="red" inherit>*</Text>
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" mb={8}>
                      Write email-safe HTML. Use variables like {'{{patient.firstName}}'}. Scripts, event handlers,
                      unsafe links, and unsupported tags will be blocked on save.
                    </Text>
                    {validationErrors['htmlBody'] && (
                      <Text size="xs" c="red" mb={4}>{validationErrors['htmlBody']}</Text>
                    )}
                    <NotificationHtmlEditor
                      value={draft.htmlBody}
                      onChange={(html) => update({ htmlBody: html })}
                    />
                  </Box>

                  {/* Plain text fallback — uses shared VariableTextarea */}
                  <Box>
                    <Group justify="space-between" align="center" mb={4}>
                      <Text size="sm" fw={500}>Plain text fallback (optional)</Text>
                      <Tooltip label="Generate plain text from current HTML body" position="left">
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          leftSection={<IconFileText size={14} />}
                          onClick={() => update({ textBody: htmlToPlainText(draft.htmlBody) })}
                        >
                          Generate from HTML
                        </Button>
                      </Tooltip>
                    </Group>
                    <VariableTextarea
                      label=""
                      placeholder="Plain text version of the email body"
                      value={draft.textBody}
                      onChange={(next) => update({ textBody: next })}
                      pickerPosition="header"
                      autosize
                      minRows={3}
                      maxRows={8}
                    />
                  </Box>
                </Stack>
              </Paper>

              {/* Custom variables (advanced, collapsible) */}
              <CustomVariablesSection
                variables={draft.variables}
                onChange={(variables) => update({ variables })}
              />
            </Stack>
          </Tabs.Panel>

          {/* ======================== Preview Tab ======================== */}
          <Tabs.Panel value="preview" pt="lg">
            <PreviewPanel
              subject={preview.subject}
              htmlBody={sanitizedPreviewHtml}
              textBody={preview.textBody}
              fromName={draft.from.name ?? ''}
              fromEmail={draft.from.email ?? ''}
              unresolvedRequired={preview.unresolvedRequired}
              draft={draft}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// HTML source editor with variable picker
// ---------------------------------------------------------------------------

function NotificationHtmlEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInsertVariable = useCallback(
    (token: string) => {
      const el = textareaRef.current;
      if (!el) {
        onChange(value + token);
        return;
      }
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? start;
      const before = value.slice(0, start);
      const after = value.slice(end);
      const next = before + token + after;
      onChange(next);
      requestAnimationFrame(() => {
        el.focus();
        const cursorPos = start + token.length;
        el.setSelectionRange(cursorPos, cursorPos);
      });
    },
    [value, onChange],
  );

  return (
    <Box>
      <Group gap="xs" mb={6}>
        <VariablePickerPopover onPick={handleInsertVariable} triggerLabel="Insert variable" />
      </Group>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        placeholder={`<table width="100%" cellpadding="0" cellspacing="0">\n  <tr><td style="padding:32px">\n    <p>Hi {{patient.firstName}},</p>\n    <p>Your content here.</p>\n  </td></tr>\n</table>`}
        autosize
        minRows={16}
        maxRows={40}
        styles={{
          input: {
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
          },
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Plain text generation from HTML
// ---------------------------------------------------------------------------

export function htmlToPlainText(html: string): string {
  if (!html.trim()) return '';
  let text = html;
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/p>/gi, '\n\n');
  text = text.replace(/<\/div>/gi, '\n');
  text = text.replace(/<\/tr>/gi, '\n');
  text = text.replace(/<\/li>/gi, '\n');
  text = text.replace(/<li[^>]*>/gi, '• ');
  text = text.replace(/<a[^>]+href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi, '$2 ($1)');
  text = text.replace(/<[^>]+>/g, '');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&#39;/g, "'");
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/\n{3,}/g, '\n\n');
  return text.trim();
}

// ---------------------------------------------------------------------------
// Custom variables (advanced, collapsible section)
// ---------------------------------------------------------------------------

function CustomVariablesSection({
  variables,
  onChange,
}: {
  variables: NotificationTemplateVariable[];
  onChange: (variables: NotificationTemplateVariable[]) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(variables.length > 0);

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group
          justify="space-between"
          style={{ cursor: 'pointer' }}
          onClick={() => setExpanded((v) => !v)}
        >
          <Group gap="xs">
            {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            <Title order={5}>Custom Variables</Title>
            {variables.length > 0 && (
              <Badge size="sm" variant="light" color="blue">{variables.length}</Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            For variables not in the standard catalog
          </Text>
        </Group>

        <Collapse in={expanded}>
          <Stack gap="sm" pt="xs">
            <Text size="xs" c="dimmed">
              Standard variables (patient, provider, appointment, etc.) are available automatically from the variable picker.
              Add custom variables here only for additional template-specific data.
            </Text>

            {variables.map((v, idx) => (
              <VariableRow
                key={idx}
                variable={v}
                onChange={(updated) => {
                  const vars = [...variables];
                  vars[idx] = updated;
                  onChange(vars);
                }}
                onRemove={() => {
                  onChange(variables.filter((_, i) => i !== idx));
                }}
              />
            ))}

            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                onChange([...variables, { key: '', label: '', required: false, example: '' }]);
              }}
              style={{ alignSelf: 'flex-start' }}
            >
              Add custom variable
            </Button>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Variable row
// ---------------------------------------------------------------------------

function VariableRow({
  variable,
  onChange,
  onRemove,
}: {
  variable: NotificationTemplateVariable;
  onChange: (v: NotificationTemplateVariable) => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <Paper withBorder p="sm" radius="sm">
      <Group gap="sm" align="flex-start" wrap="wrap">
        <TextInput
          label="Key"
          placeholder="custom.fieldName"
          value={variable.key}
          onChange={(e) => onChange({ ...variable, key: e.currentTarget.value })}
          style={{ flex: 1, minWidth: 150 }}
          size="xs"
        />
        <TextInput
          label="Label"
          placeholder="Field display name"
          value={variable.label}
          onChange={(e) => onChange({ ...variable, label: e.currentTarget.value })}
          style={{ flex: 1, minWidth: 150 }}
          size="xs"
        />
        <TextInput
          label="Description"
          placeholder="What this variable contains"
          value={variable.description ?? ''}
          onChange={(e) => onChange({ ...variable, description: e.currentTarget.value })}
          style={{ flex: 1, minWidth: 150 }}
          size="xs"
        />
        <TextInput
          label="Example"
          placeholder="Preview value"
          value={variable.example ?? ''}
          onChange={(e) => onChange({ ...variable, example: e.currentTarget.value })}
          style={{ flex: 1, minWidth: 100 }}
          size="xs"
        />
        <TextInput
          label="Resource path"
          placeholder="name[0].given[0]"
          value={variable.resourcePath ?? ''}
          onChange={(e) => onChange({ ...variable, resourcePath: e.currentTarget.value })}
          style={{ flex: 1, minWidth: 150 }}
          size="xs"
        />
        <Box pt={24}>
          <Switch
            label="Required"
            checked={variable.required}
            onChange={(e) => onChange({ ...variable, required: e.currentTarget.checked })}
            size="xs"
          />
        </Box>
        <Box pt={22}>
          <ActionIcon color="red" variant="subtle" size="sm" onClick={onRemove}>
            <IconTrash size={14} />
          </ActionIcon>
        </Box>
      </Group>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Preview panel (subject, body, plain text) — sandboxed iframe
// ---------------------------------------------------------------------------

function PreviewPanel({
  subject,
  htmlBody,
  textBody,
  fromName,
  fromEmail,
  unresolvedRequired,
  draft,
}: {
  subject: string;
  htmlBody: string;
  textBody: string;
  fromName: string;
  fromEmail: string;
  unresolvedRequired: string[];
  draft: NotificationTemplateDraft;
}): JSX.Element {
  const medplum = useMedplum();
  // Test sends go only to the signed-in user's own email (email-sender `send-test-email`, ENG-3764 D24).
  const [testEmail, setTestEmail] = useState(
    () => medplum.getProfile()?.telecom?.find((t) => t.system === 'email')?.value ?? '',
  );
  const [sendingTest, setSendingTest] = useState(false);
  const rbac = usePermissions();
  // send-test-email executes for a real unify-admin only (the assigned slug, not the display role).
  const canTestEmail = rbac.ready && canSendTestEmail(rbac.availableRoles);

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      notify({ title: 'Error', message: 'Enter a test recipient email.', type: 'error' });
      return;
    }

    setSendingTest(true);
    try {
      const templateConfig: NotificationTemplateConfig = {
        id: draft.id ?? 'test-draft',
        version: draft.version,
        status: draft.status,
        name: draft.name,
        description: draft.description || undefined,
        channel: draft.channel,
        from: draft.from,
        subject: draft.subject,
        htmlBody: draft.htmlBody,
        textBody: draft.textBody || undefined,
        variables: draft.variables,
      };

      const result = await sendTestEmail(medplum, {
        templateConfig,
        recipientEmail: testEmail.trim(),
        isTest: true,
      });

      if (result.ok) {
        notify({
          title: 'Test email sent',
          message: 'A [TEST] copy was sent to your email. Check your inbox to verify delivery.',
          type: 'success',
        });
      } else {
        notify({ title: 'Failed', message: result.error ?? 'Unknown error', type: 'error' });
      }
    } catch (err) {
      notify({
        title: 'Error',
        error: err,
        message: 'Failed to send test email',
        type: 'error',
      });
    } finally {
      setSendingTest(false);
    }
  };

  const iframeSrcDoc = useMemo(() => {
    if (!htmlBody) return '<p style="color: #999; font-family: sans-serif">Email body preview will appear here</p>';
    return htmlBody;
  }, [htmlBody]);

  return (
    <Stack gap="lg">
      {unresolvedRequired.length > 0 && (
        <Paper withBorder p="sm" radius="sm" style={{ borderColor: 'var(--mantine-color-yellow-6)' }}>
          <Text size="sm" fw={500} c="yellow.8">
            Unresolved required variables: {unresolvedRequired.join(', ')}
          </Text>
          <Text size="xs" c="dimmed">
            Add example values to these variables to see a complete preview.
          </Text>
        </Paper>
      )}

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Group gap="sm">
            <Text size="xs" fw={600} c="dimmed" w={60}>From:</Text>
            <Text size="sm">{fromName ? `${fromName} <${fromEmail}>` : fromEmail || '—'}</Text>
          </Group>
          <Group gap="sm">
            <Text size="xs" fw={600} c="dimmed" w={60}>Subject:</Text>
            <Text size="sm" fw={500}>{subject || '—'}</Text>
          </Group>
          <Divider />
          <Text size="xs" c="dimmed">Preview shows sanitized HTML — scripts and unsafe content are stripped.</Text>
          <iframe
            srcDoc={iframeSrcDoc}
            sandbox=""
            title="Email preview"
            style={{
              width: '100%',
              minHeight: 300,
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 8,
              backgroundColor: '#fff',
            }}
          />
        </Stack>
      </Paper>

      {textBody && (
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Text size="sm" fw={500}>Plain Text Fallback</Text>
            <Box
              style={{
                padding: 12,
                backgroundColor: 'var(--mantine-color-gray-0)',
                borderRadius: 6,
                fontFamily: 'monospace',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
              }}
            >
              {textBody}
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Send test email */}
      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Title order={5}>Send Test Email</Title>
          <Text size="xs" c="dimmed">
            Send a test email using example variable values. The email goes through the same
            email-sender bot pipeline as production sends. Check the recipient inbox to verify
            delivery.
          </Text>
          <Group gap="sm" align="flex-end">
            <TextInput
              label="Recipient email"
              description={
                canTestEmail ? 'Test emails go to your own email address' : 'Only a Unify Admin can send a test email'
              }
              placeholder="you@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              color="blue"
              leftSection={<IconMail size={16} />}
              onClick={handleSendTest}
              loading={sendingTest}
              disabled={!canTestEmail || !draft.subject.trim() || !draft.htmlBody.trim()}
            >
              Send test
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
